"use client";

import type { ReactNode } from "react";
import { useTranslations } from "@zapier/i18n";
import { Text } from "@zapier/design-system";
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from "react-resizable-panels";
import { useSplitTreatments } from "@zapier/client";
import { ConnectionsSdkCredentialsNav } from "./ConnectionsSdkCredentialsNav";
import { useEmitAssetSidebarResizeEvent } from "../../../events/useEmitAssetSidebarResizeEvent";
import styles from "../AssetLayout/AssetLayout.module.css";

export function ConnectionsSdkCredentialsLayout({
  children,
}: {
  children: ReactNode;
}) {
  const t = useTranslations("asset-layout");
  const { emit: emitResizeEvent } = useEmitAssetSidebarResizeEvent();
  const { isSdkCredentialsEnabled, isLoaded } = useSplitTreatments();
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: "connections-sdk-credentials-layout",
    storage: localStorage,
  });

  // While the flag is loading, or once it resolves off, there's no switcher
  // to show, so no sidebar to show either -- same as if this layout were
  // never applied. Rendering children immediately (rather than blanking the
  // page until isLoaded) avoids a flash of nothing for the common case where
  // the flag ends up off; the tradeoff is a layout shift from single- to
  // two-pane in the less common case where it resolves on.
  if (!isLoaded || !isSdkCredentialsEnabled) {
    return children;
  }

  return (
    <>
      <div className={styles.mobileNav}>
        <ConnectionsSdkCredentialsNav />
      </div>
      <Group
        className={styles.layout}
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel defaultSize={"15%"} minSize={"10%"} maxSize={"30%"} id="left">
          <div className={styles.sidebar}>
            <div className={styles.header}>
              <Text tag="h2" type="SectionHeader">
                {t("headings.connections")}
              </Text>
            </div>

            <ConnectionsSdkCredentialsNav />
          </div>
        </Panel>

        <Separator
          className={styles.resizeHandle}
          aria-label={t("resize-handle.label")}
          onDragEnd={(event) => emitResizeEvent(event.clientX)}
        />

        <Panel className={styles.contentPanel} id="right">
          <div className={styles.content}>{children}</div>
        </Panel>
      </Group>
    </>
  );
}
